import { useMemo } from 'react';
import { HouseFill } from 'react-bootstrap-icons';
import { BreadcrumbComponentType } from 'use-react-router-breadcrumbs';

import { useBreadcrumbExternalRedirectsQuery } from '@/modules/ExternalRedirects';
import { useBreadcrumbPageComponentQuery, useBreadcrumbPageLayoutsQuery, useBreadcrumbPagesQuery } from '@/modules/Pages';
import { useBreadcrumbRedirectsQuery, useBreadcrumbReferralsQuery, useBreadcrumbSitesQuery } from '@/modules/Sites';

import { DynamicBreadcrumbWrapper } from './DynamicBreadcrumbWrapper';


const DynamicSitesByIdBreadcrumb: BreadcrumbComponentType<'siteId'> = ({ match }) => {
  const siteId = useMemo(() => match.params.siteId ?? '', [match.params.siteId]);

  const breadcrumbQuery = useBreadcrumbSitesQuery(siteId)();

  return <DynamicBreadcrumbWrapper { ...breadcrumbQuery }/>;
};

const DynamicSitesListBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Сайти</span>;
};

const DynamicSitesFsManagerBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Файловий менеджер</span>;
};

const DynamicSitesDomainsListBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Домени</span>;
};

const DynamicPagesListPagesBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Сторінки</span>;
};

const DynamicPagesListStructureBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Структура</span>;
};

const DynamicPageNodeEditBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Редагувати</span>;
};

const DynamicPageNodeCreateBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Створити</span>;
};

const DynamicPagesByIdBreadcrumb: BreadcrumbComponentType<'pageId' | 'siteId'> = ({ match }) => {
  const siteId = useMemo(() => match.params.siteId ?? '', [match.params.siteId]);
  const pageId = useMemo(() => match.params.pageId ?? '', [match.params.pageId]);

  const breadcrumbQuery = useBreadcrumbPagesQuery(pageId, siteId)();

  return (
    <DynamicBreadcrumbWrapper
      { ...breadcrumbQuery }
      data={ breadcrumbQuery.data === '/' ? <HouseFill/> : breadcrumbQuery.data }
    />
  );
};

const DynamicSitesTemplatesBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Шаблонізація</span>;
};

const DynamicSitesCriticalCssBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Critical CSS</span>;
};

const DynamicPageLayoutsBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Макети</span>;
};

const DynamicPageLayoutsByIdBreadcrumb: BreadcrumbComponentType<'layoutId' | 'siteId'> = ({ match }) => {
  const siteId = useMemo(() => match.params.siteId ?? '', [match.params.siteId]);
  const layoutId = useMemo(() => match.params.layoutId ?? '', [match.params.layoutId]);

  const breadcrumbQuery = useBreadcrumbPageLayoutsQuery(layoutId, siteId)();

  return <DynamicBreadcrumbWrapper { ...breadcrumbQuery }/>;
};

const DynamicPageComponentsBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Компоненти</span>;
};

const DynamicPageComponentsByIdBreadcrumb: BreadcrumbComponentType<'componentId' | 'siteId'> = ({ match }) => {
  const siteId = useMemo(() => match.params.siteId ?? '', [match.params.siteId]);
  const componentId = useMemo(() => match.params.componentId ?? '', [match.params.componentId]);

  const breadcrumbQuery = useBreadcrumbPageComponentQuery(componentId, siteId)();

  return <DynamicBreadcrumbWrapper { ...breadcrumbQuery }/>;
};

const DynamicRobotsBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Robots</span>;
};

const DynamicSitemapBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Файли індексації</span>;
};

const DynamicSearchConsoleBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Search Console</span>;
};

const DynamicSearchConsoleSitemapBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Sitemap</span>;
};

const DynamicSearchConsoleAnalyticsBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Analytics</span>;
};

const DynamicSearchConsoleUrlInspectionBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Url Inspection</span>;
};

const DynamicReferralsBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Реферали</span>;
};

const DynamicReferralsByIdBreadcrumb: BreadcrumbComponentType<'siteId' | 'referralId'> = ({ match }) => {
  const siteId = useMemo(() => match.params.siteId ?? '', [match.params.siteId]);
  const referralId = useMemo(() => match.params.referralId ?? '', [match.params.referralId]);

  const breadcrumbQuery = useBreadcrumbReferralsQuery(siteId, referralId)();

  return <DynamicBreadcrumbWrapper { ...breadcrumbQuery }/>;
};

const DynamicRedirectsBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Редіректи</span>;
};

const DynamicRedirectsByIdBreadcrumb: BreadcrumbComponentType<'siteId' | 'redirectId'> = ({ match }) => {
  const siteId = useMemo(() => match.params.siteId ?? '', [match.params.siteId]);
  const redirectId = useMemo(() => match.params.redirectId ?? '', [match.params.redirectId]);

  const breadcrumbQuery = useBreadcrumbRedirectsQuery(siteId, redirectId)();

  return <DynamicBreadcrumbWrapper { ...breadcrumbQuery }/>;
};

const DynamicExternalRedirectsListBreadcrumb: BreadcrumbComponentType = () => {
  return <span>Зовнішні редіректи</span>;
};

const DynamicExternalRedirectsByIdBreadcrumb: BreadcrumbComponentType<'siteId' | 'externalRedirectId'> = ({ match }) => {
  const siteId = useMemo(() => match.params.siteId ?? '', [match.params.siteId]);
  const externalRedirectId = useMemo(() => match.params.externalRedirectId ?? '', [match.params.externalRedirectId]);

  const breadcrumbQuery = useBreadcrumbExternalRedirectsQuery(siteId, externalRedirectId)();

  return <DynamicBreadcrumbWrapper { ...breadcrumbQuery }/>;
};

export const DynamicSitesBreadcrumb = {
  DynamicSitesByIdBreadcrumb,
  DynamicSitesListBreadcrumb,
  DynamicPagesListPagesBreadcrumb,
  DynamicPagesListStructureBreadcrumb,
  DynamicPageNodeEditBreadcrumb,
  DynamicPageNodeCreateBreadcrumb,
  DynamicPagesByIdBreadcrumb,
  DynamicSitesFsManagerBreadcrumb,
  DynamicSitesDomainsListBreadcrumb,
  DynamicSitesTemplatesBreadcrumb,
  DynamicSitesCriticalCssBreadcrumb,
  DynamicPageLayoutsBreadcrumb,
  DynamicPageLayoutsByIdBreadcrumb,
  DynamicPageComponentsBreadcrumb,
  DynamicPageComponentsByIdBreadcrumb,
  DynamicRobotsBreadcrumb,
  DynamicSitemapBreadcrumb,
  DynamicSearchConsoleBreadcrumb,
  DynamicSearchConsoleSitemapBreadcrumb,
  DynamicSearchConsoleAnalyticsBreadcrumb,
  DynamicSearchConsoleUrlInspectionBreadcrumb,
  DynamicReferralsBreadcrumb,
  DynamicReferralsByIdBreadcrumb,
  DynamicRedirectsBreadcrumb,
  DynamicRedirectsByIdBreadcrumb,
  DynamicExternalRedirectsListBreadcrumb,
  DynamicExternalRedirectsByIdBreadcrumb
};
